import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import { selectTranslations } from '../../services/i18n/i18nSlice'
import { OFA_PASTE_COLUMNS, parseOfaPaste } from './ofaPaste'

const STATUS_LABEL = {
    new: <span className="text-success">Nuovo</span>,
    update: <span className="text-primary">Aggiorna</span>,
    invalid: <span className="text-danger">Errore</span>,
}

const PreviewCell = ({ col, row, groupsById }) => {
    const error = row.errors[col.key]
    const raw = row.raw[col.key]
    let content
    if(col.kind === 'subjects'){
        content = row.record.subjects.map(s => s.subject_name).join(', ')
    } else if(col.kind === 'group' && raw && !error){
        content = <div title={row.record[col.key]}>{groupsById[row.record[col.key]] || row.record[col.key]}</div>
    } else {
        content = raw
    }
    return (
        <td className={error ? 'table-danger' : undefined}>
            {content || (!error && <span className="text-muted">—</span>)}
            {error ? <div><small className="text-danger">{error}</small></div> : null}
        </td>
    )
}

export default function OfaPasteModal({ show, groupsById, subjects, existing, onClose, onConfirm }){
    const t = useSelector(selectTranslations)
    const [text, setText] = useState('')

    useEffect(()=>{ if(show) setText('') },[show])

    const { hasHeader, rows } = useMemo(
        () => parseOfaPaste(text, { groupsById, subjects, existing }),
        [text, groupsById, subjects, existing]
    )
    const validRecords = rows.filter(r => r.status !== 'invalid').map(r => r.record)

    return (
        <Modal show={show} onHide={onClose} keyboard={false} centered size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Incolla da Excel</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted mb-2">
                    Copia le righe da Excel e incollale qui. Se la prima riga contiene i nomi delle colonne, l'ordine è libero;
                    altrimenti le colonne devono essere in quest'ordine. Una riga con stesso gruppo, Test ID e AA ID di una esistente la aggiorna; con Test ID o AA ID diversi viene aggiunta come nuova riga dello stesso gruppo.
                </p>
                <div className="table-responsive mb-3">
                    <table className="table table-sm table-bordered mb-0">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Colonna</th>
                                <th>Contenuto</th>
                                <th>Esempio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {OFA_PASTE_COLUMNS.map((c, idx) => (
                                <tr key={c.key}>
                                    <td>{String.fromCharCode(65 + idx)}</td>
                                    <td><strong>{c.label}</strong>{c.required ? ' *' : ''}</td>
                                    <td>{c.hint}</td>
                                    <td><code>{c.example}</code></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <small className="text-muted">* obbligatoria. Le altre colonne possono restare vuote.</small>
                </div>
                <Form.Control
                    as="textarea"
                    rows={6}
                    value={text}
                    onChange={(e)=>setText(e.target.value)}
                    placeholder={OFA_PASTE_COLUMNS.map(c => c.label).join('\t')}
                    style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
                />

                {rows.length ? (
                    <>
                        <div className="mt-3 mb-2">
                            <small className="text-muted">
                                {hasHeader ? 'Intestazione riconosciuta. ' : 'Nessuna intestazione: uso l\'ordine predefinito. '}
                                {validRecords.length} righe valide, {rows.length - validRecords.length} con errori.
                            </small>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-sm align-middle">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Stato</th>
                                        {OFA_PASTE_COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr key={row.line}>
                                            <td><small className="text-muted">{row.line}</small></td>
                                            <td>{STATUS_LABEL[row.status]}</td>
                                            {OFA_PASTE_COLUMNS.map(c => <PreviewCell key={c.key} col={c} row={row} groupsById={groupsById} />)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" onClick={onClose}>{t?.modals?.abort || 'Annulla'}</button>
                <button type="button" disabled={!validRecords.length} onClick={()=>onConfirm(validRecords)}>
                    {`Importa ${validRecords.length} righe valide`}
                </button>
            </Modal.Footer>
        </Modal>
    )
}
